import { mapToCamelCase } from '@consigli/utils';
import { z } from 'zod';
export var MeetingSecretaryType;
(function (MeetingSecretaryType) {
    MeetingSecretaryType["MEETING_SECRETARY"] = "MEETING_SECRETARY";
})(MeetingSecretaryType || (MeetingSecretaryType = {}));
// Recurring series schema
export const recurringSeriesSchema = z
    .object({
    id: z.string().uuid(),
    title: z.string(),
    description: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
})
    .strict()
    .transform(mapToCamelCase);
const meetingUserRoleSchemaRaw = z.object({
    id: z.string().uuid(),
    email: z.string(),
    name: z.string().nullable().optional(),
    project_id: z.number(),
    consigli_user_id: z.string().uuid().nullable().optional(),
    created_at: z.string(),
    role: z.string(),
});
export const meetingSchema = z
    .object({
    id: z.string().uuid(),
    consigli_user_id: z.string().uuid().nullish(),
    title: z.string(),
    description: z.string().nullish(),
    scheduled_start_time: z.string(),
    scheduled_end_time: z.string(),
    location: z.string().nullish(),
    organizer_email: z.string().optional(),
    join_url: z.string().url(),
    participants: z.array(meetingUserRoleSchemaRaw).optional(),
    document_package_id: z.string().uuid().nullish(),
    project_id: z.number().nullish(),
    recording_completed: z.boolean().nullish(),
    transcript_retrieval_completed: z.boolean().nullish(),
    has_transcription: z.boolean().nullish(),
    raw_ms_graph_meeting: z.record(z.any()).optional(),
    created_at: z.string(),
    updated_at: z.string(),
    calendar_event_id: z.string().nullish(),
    created_by_assistant: z.boolean().nullish(),
    recurring_series_id: z.string().uuid().nullish(),
    recurring_series: recurringSeriesSchema.nullish(),
    recurring_series_title: z.string().nullish(),
    is_active: z.boolean().optional(),
    real_start_time: z.string().nullish(),
    real_end_time: z.string().nullish(),
    timezone: z.string().nullish(),
})
    .passthrough()
    .transform(mapToCamelCase);
// Schema for the full response object with metadata
const fullMeetingsResponseSchema = z
    .object({
    meetings: z.array(meetingSchema),
    total: z.number(),
    api_source: z.string(),
    transcript_support: z.boolean(),
    authentication_type: z.string(),
    note: z.string(),
    meeting_discovery: z.object({
        total_events_checked: z.string(),
        online_meetings_found: z.number(),
        id_format: z.string(),
    }),
    transcript_limitation: z.object({
        issue: z.string(),
        solution: z.string(),
    }),
})
    .strict()
    .transform(mapToCamelCase);
// Schema that accepts either an array of meetings or the full response object
export const getMeetingsResponseSchema = z.union([
    z.array(meetingSchema).transform((meetings) => ({
        meetings,
        total: meetings.length,
        api_source: 'database',
        transcript_support: true,
        authentication_type: 'oauth',
        note: '',
        meeting_discovery: {
            total_events_checked: '0',
            online_meetings_found: meetings.length,
            id_format: 'uuid',
        },
        transcript_limitation: {
            issue: '',
            solution: '',
        },
    })),
    fullMeetingsResponseSchema,
]);
export const userMeetingStatisticsSchema = z
    .object({
    total_action_items: z.number(),
    total_meetings: z.number(),
    total_meeting_hours: z.number(),
})
    .strict()
    // the map to camelcase function makes this wierd underscore for this naming so better to do it manually in this case
    .transform((data) => ({
    totalActionItems: data.total_action_items,
    totalMeetings: data.total_meetings,
    totalMeetingHours: data.total_meeting_hours,
}));
export const transcriptSegmentSchema = z
    .object({
    id: z.string().uuid(),
    speaker_name: z.string().nullable().optional(),
    cleaned_transcription_text: z.string().nullable().optional(),
    start_ms: z.number().nullable().optional(),
    end_ms: z.number().nullable().optional(),
    locale: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
    // Set once a segment is linked to a real MeetingUser. The backend clears
    // `speaker_name` and resolves the display name into `assigned_user_name`.
    assigned_meeting_user_id: z.string().uuid().nullable().optional(),
    assigned_user_name: z.string().nullable().optional(),
    // legacy fields kept for backward compatibility
    speaker: z.string().optional(),
    transcription_text: z.string().optional(),
    enriched_transcription_text: z.string().nullable().optional(),
})
    .passthrough()
    .transform(mapToCamelCase);
// Speaker assignment (PATCH /meetings/{id}/segments/{id}/speaker) — controls
// whether a single segment or every segment sharing the same pipeline speaker
// label gets reassigned to the chosen MeetingUser.
export const speakerAssignScopeSchema = z.enum(['single', 'all_same_name', 'all_same_user']);
export const assignSpeakerResponseSchema = z
    .object({
    updated_count: z.number(),
})
    .transform(mapToCamelCase);
export const transcriptSchema = z
    .object({
    id: z.string(),
    meeting_id: z.string().optional(),
    content: z.string().nullable().optional(),
    full_raw_text: z.string().optional(),
    source: z.string().optional(),
    creation_timestamp: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    createdDateTime: z.string().optional(),
    segments: z.array(transcriptSegmentSchema).optional(),
})
    .passthrough()
    .transform(mapToCamelCase);
const transcriptsWrapperSchema = z
    .object({
    transcripts: z.array(transcriptSchema),
    message_key: z.string().optional(),
    // Only on a transcript list served straight from the database. True for the
    // organizer while the meeting still has text-only segments.
    can_regenerate_with_audio: z.boolean().optional(),
    // A re-run found no recording, or the meeting belongs to another tenant: the audio cannot be
    // had, for anyone. Absent when the response never computes it, which is not the same as false.
    audio_unavailable: z.boolean().optional(),
    audio_unavailable_reason: z.enum(['no_recording', 'external_organizer']).optional(),
})
    .transform(mapToCamelCase);
export const getTranscriptsResponseSchema = z
    .object({
    transcripts: transcriptsWrapperSchema,
    error: z.string().optional(),
    user_friendly_message: z.string().optional(),
    message: z.string().optional(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const meetingSummarySchema = z
    .object({
    generated_summary_text: z.string(),
    // The two editable sections, raw. Optional so an older backend still parses.
    summary_text: z.string().optional(),
    key_discussion_points_text: z.string().optional(),
    updated_at: z.string().nullish(),
    key_discussion_points: z.array(z.string()).optional(),
    related_documents: z.array(z.string()).optional(),
    start: z.string().nullish(),
    end: z.string().nullish(),
    location: z.string().nullish(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const meetingSummaryResponseSchema = z.nullable(meetingSummarySchema);
// A person responsible for something (agreement party / action-item owner).
// Exactly one of the two ids is set; `name` is the resolved display name.
export const responsiblePartySchema = z
    .object({
    meeting_user_id: z.string().nullable(),
    custom_party_id: z.string().nullable(),
    name: z.string().nullable(),
})
    .transform(mapToCamelCase);
export const actionItemSchema = z
    .object({
    // Backend may return either 'id' or 'action_item_id'
    id: z.string().nullish(),
    action_item_id: z.string().nullish(),
    description_text: z.string(),
    completion_status: z.string(),
    // Single responsible person (null when unassigned).
    responsible_person: responsiblePartySchema.nullish(),
    due_date: z.string().nullish(),
    topic: z.string().nullish(),
    status: z.string().nullish(),
    supersedes_id: z.string().nullish(),
    is_active: z.boolean().nullish(),
    superseded_by_id: z.string().nullish(),
    source_transcript_id: z.string().nullish(),
    source_segment_ids: z.array(z.string()).nullish(),
})
    .passthrough()
    .refine((data) => !!(data.id ?? data.action_item_id), {
    message: 'ActionItem must have either id or action_item_id',
})
    .transform((data) => {
    const normalized = {
        ...data,
        // Cast is safe: refine above guarantees one of these is defined
        id: (data.id ?? data.action_item_id),
    };
    return mapToCamelCase(normalized);
});
export const getActionItemsResponseSchema = z.array(actionItemSchema);
// Single action item response (for create/update operations)
export const actionItemResponseSchema = actionItemSchema;
// Project-level action item schema (includes meeting context)
export const projectActionItemSchema = z
    .object({
    id: z.string(),
    meeting_id: z.string(),
    description_text: z.string(),
    responsible_person: responsiblePartySchema.nullish(),
    completion_status: z.string(),
    source_transcript_id: z.string().nullish(),
    source_segment_ids: z.array(z.string()).nullish(),
    creation_timestamp: z.string(),
    due_date: z.string().nullish(),
    topic: z.string().nullish(),
    meeting_title: z.string().nullish(),
    meeting_scheduled_start_time: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
    status: z.string().nullish(),
    supersedes_id: z.string().nullish(),
    is_active: z.boolean().nullish(),
    superseded_by_id: z.string().nullish(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const getProjectActionItemsResponseSchema = z.array(projectActionItemSchema);
export const meetingAgreementSchema = z
    .object({
    id: z.string(),
    meeting_id: z.string(),
    project_id: z.number(),
    agreement: z.string(),
    source_text_from_transcript: z.string().nullish(),
    source_segment_ids: z.array(z.string()).nullish(),
    enriched_agreement: z.string().nullish(),
    responsible_parties: z.array(responsiblePartySchema).nullish().default([]),
    aux_agreement_info: z.array(z.string()).nullish(),
    has_meeting_contradictions: z.boolean().nullish(),
    has_document_contradictions: z.boolean().nullish(),
    active: z.boolean().nullish(),
    created_by: z.enum(['meeting_assistant', 'user']).nullish(),
    created_at: z.string().nullish(),
    updated_at: z.string().nullish(),
    created_by_user_id: z.string().nullish(),
    updated_by_user_id: z.string().nullish(),
    // Built from a text-only transcript: only its own meeting sees it, until audio replaces it.
    provisional: z.boolean().nullish(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const getAgreementsResponseSchema = z.array(meetingAgreementSchema);
export const generateAgreementsResponseSchema = z
    .object({
    agreements: z.array(meetingAgreementSchema).optional(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const contradictionLinkSchema = z
    .object({
    contradiction_id: z.string(),
    agreement_id: z.string(),
    confidence_score: z.number().optional(),
    contradiction_severity: z.enum(['low', 'medium', 'high']).optional(),
    contradiction_reason: z.string().optional(),
    contradiction_source_text: z.string().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    agreement_text: z.string().optional(),
    responsible_parties: z.array(responsiblePartySchema).nullish().default([]),
    meeting_datetime: z.string().nullish(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const agreementContradictionSchema = z
    .object({
    id: z.string(),
    project_id: z.number(),
    meeting_id: z.string().nullish(),
    conflict_type: z.string().optional(),
    title: z.string().optional(),
    contradiction_description: z.string().optional(),
    status: z.enum(['active', 'resolved', 'dismissed']).optional(),
    notes: z.string().nullish(),
    resolved_by_user_id: z.string().nullish(),
    resolved_at: z.string().nullish(),
    expires_at: z.string().nullish(),
    provisional: z.boolean().nullish(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    agreement_links: z.array(contradictionLinkSchema).optional(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const getAgreementContradictionsResponseSchema = z.array(agreementContradictionSchema);
export const documentContradictionAgreementLinkSchema = z
    .object({
    contradiction_id: z.string(),
    agreement_id: z.string(),
    agreement_text: z.string(),
    confidence_score: z.number().nullish(),
    contradiction_severity: z.enum(['low', 'medium', 'high']).nullish(),
    contradiction_reason: z.string().nullish(),
    contradiction_source_text: z.string().nullish(),
    responsible_parties: z.array(responsiblePartySchema).nullish().default([]),
    meeting_datetime: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const documentContradictionChunkLinkSchema = z
    .object({
    id: z.string(),
    contradiction_id: z.string(),
    document_package_id: z.string(),
    blob_id: z.string().nullish(),
    chunk_text: z.string(),
    highlight_text: z.string().nullish(),
    el_numbers: z.array(z.number()).nullish(),
    file_name: z.string().nullish(),
    page_numbers: z.array(z.number()).nullish(),
    /** 0-based page of the bounding box below, in the same space as ProcessorFinding. */
    page_index: z.number().nullish(),
    x0: z.number().nullish(),
    y0: z.number().nullish(),
    x1: z.number().nullish(),
    y1: z.number().nullish(),
    confidence_score: z.number().nullish(),
    contradiction_severity: z.enum(['low', 'medium', 'high']).nullish(),
    contradiction_reason: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const documentContradictionSchema = z
    .object({
    id: z.string(),
    project_id: z.number(),
    meeting_id: z.string().nullish(),
    conflict_type: z.string(),
    title: z.string(),
    contradiction_description: z.string(),
    status: z.enum(['active', 'resolved', 'dismissed']),
    notes: z.string().nullish(),
    resolved_by_user_id: z.string().nullish(),
    resolved_at: z.string().nullish(),
    expires_at: z.string().nullish(),
    provisional: z.boolean().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
    agreement_links: z.array(documentContradictionAgreementLinkSchema).default([]),
    chunk_links: z.array(documentContradictionChunkLinkSchema).default([]),
})
    .passthrough()
    .transform(mapToCamelCase);
export const getDocumentContradictionsResponseSchema = z.array(documentContradictionSchema);
// Recurring meeting creation schemas - aligned with backend API
export const recurrencePatternTypeSchema = z.enum([
    'daily',
    'weekly',
    'absoluteMonthly',
    'relativeMonthly',
    'absoluteYearly',
    'relativeYearly',
]);
export const recurrenceRangeTypeSchema = z.enum(['endDate', 'noEnd', 'numbered']);
export const recurrencePatternSchema = z.object({
    type: recurrencePatternTypeSchema,
    interval: z.number().min(1),
    days_of_week: z.array(z.string()).optional(),
    day_of_month: z.number().min(1).max(31).optional(),
});
export const recurrenceRangeSchema = z.object({
    type: recurrenceRangeTypeSchema,
    start_date: z.string(), // YYYY-MM-DD format
    end_date: z.string().optional(), // for endDate type
    number_of_occurrences: z.number().min(1).optional(), // for numbered type
});
export const recurringMeetingCreateSchema = z.object({
    title: z.string().min(1).max(500),
    description: z.string().optional(),
    location: z.string().optional(),
    start_time: z.string(), // HH:MM format
    end_time: z.string(), // HH:MM format
    timezone: z.string().default('UTC'),
    attendees: z.array(z.string()).optional(),
    recurrence_pattern: recurrencePatternSchema,
    recurrence_range: recurrenceRangeSchema,
});
export const recurringSeriesResponseSchema = z
    .object({
    recurring_series_id: z.string().uuid(),
    title: z.string(),
    description: z.string().nullish(),
    recurrence_pattern: z.string(),
    recurrence_interval: z.number(),
    days_of_week: z.array(z.string()).nullish(),
    recurrence_end_type: z.string(),
    recurrence_end_date: z.string().nullish(),
    number_of_occurrences: z.number().nullish(),
    meetings_created: z.number(),
    calendar_event_id: z.string().optional(),
    created_at: z.string(),
})
    .passthrough()
    .transform(mapToCamelCase);
const seriesSyncErrorSchema = z
    .object({
    series_id: z.string(),
    error: z.string(),
})
    .passthrough()
    .transform(mapToCamelCase);
export const recurringSeriesSyncResponseSchema = z.array(z
    .object({
    synced_count: z.number().optional(),
    error_count: z.number().optional(),
    meetings_count: z.number().default(0),
    errors: z.array(seriesSyncErrorSchema).default([]),
    message: z.string().optional(),
    used_delta: z.boolean().default(false),
})
    .passthrough()
    .transform(mapToCamelCase));
export const taskInitiatedResponseSchema = z.object({
    task_id: z.string().min(1),
});
export const taskEnqueueResponseSchema = z.object({
    task_id: z.string().min(1),
    task_type: z.string().optional(),
});
export const SUMMARY_SEGMENTS_NOT_READY = 'segments_not_ready';
export const generateEnqueueResponseSchema = z.object({
    task_id: z.string().nullable(),
    task_type: z.string().optional(),
    already_generated: z.boolean(),
});
export const taskStatusResponseSchema = z.object({
    task_id: z.string(),
    status: z.string(),
    result: z.unknown().nullable(),
    error: z.string().nullish(),
    error_code: z.string().nullish(),
    message_key: z.string().nullish(),
    user_friendly_message: z.string().nullish(),
});
export var TaskErrorCode;
(function (TaskErrorCode) {
    TaskErrorCode["DOCUMENT_PACKAGE_PROCESSING"] = "document_package_processing";
    TaskErrorCode["DOCUMENT_PACKAGE_EMPTY"] = "document_package_empty";
})(TaskErrorCode || (TaskErrorCode = {}));
export const taskErrorBodySchema = z
    .object({
    error_code: z.string().nullish(),
    message_key: z.string().nullish(),
    user_friendly_message: z.string().nullish(),
    retriable: z.boolean().nullish(),
    detail: z.string().nullish(),
    message: z.string().nullish(),
})
    .passthrough();
const RETRIABLE_TASK_ERROR_CODES = [TaskErrorCode.DOCUMENT_PACKAGE_PROCESSING];
export const isRetriableTaskErrorCode = (code) => !!code && RETRIABLE_TASK_ERROR_CODES.includes(code);
export const projectParticipantSchema = z
    .object({
    id: z.string().uuid(),
    email: z.string(),
    name: z.string().nullable().optional(),
    project_id: z.number(),
    consigli_user_id: z.string().uuid().nullable().optional(),
    created_at: z.string(),
})
    .transform(mapToCamelCase);
export const getProjectParticipantsResponseSchema = z.array(projectParticipantSchema);
